import React, { useEffect, useMemo } from 'react';
import styles from './ErrorFallback.module.scss';
import {
  faTriangleExclamation,
  faCircleExclamation,
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import cx from 'classnames';
import { useIntl } from 'react-intl';
import { translate } from '../../utility/messageTranslator/translate';

type Props = {
  error: Error;
  resetErrorBoundary: () => void;
};

const NEW_VERSION_ERRORS = [
  // eslint-disable-next-line max-len
  /Failed to execute 'insertBefore' on 'Node': The node before which the new node is to be inserted is not a child of this node./,
  /Loading chunk [\d]+ failed/,
];

export const ErrorFallback = ({ error, resetErrorBoundary }: Props) => {
  const intl = useIntl();

  const isNewVersionError = useMemo(
    () =>
      NEW_VERSION_ERRORS.find((newVersionError) =>
        newVersionError.test(error.toString()),
      ),
    [error],
  );

  useEffect(() => {
    if (!isNewVersionError) {
      return;
    }

    setTimeout(() => {
      resetErrorBoundary();
    }, 3000);
  }, [isNewVersionError]);

  const getMessage = () => {
    if (isNewVersionError) {
      return translate(intl, 'ERROR_FALLBACK.NEW_VERSION_IS_AVAILABLE');
    }

    return translate(intl, 'ERROR_FALLBACK.UNKNOWN_ERROR');
  };

  return (
    <div className={styles.errorFallback}>
      <FontAwesomeIcon
        className={cx(styles.icon, { [styles.errorIcon]: !isNewVersionError })}
        icon={isNewVersionError ? faCircleExclamation : faTriangleExclamation}
        size="4x"
      />
      <div className={styles.errorMessage}>{getMessage()}</div>
      <div className={styles.refresh} onClick={() => resetErrorBoundary()}>
        {translate(intl, 'ERROR_FALLBACK.REFRESH')}
      </div>
    </div>
  );
};

export default ErrorFallback;
